import { SITE_TIME_ZONE, zonedDateAndMinutes, zonedTimeToInstant } from "@/app/booking-calendar/lib/siteTime"
import type {
  BookingSeasonSaveRefusal,
  BookingSeasonSnapshot,
  BookingSeasonStatus,
  SaveBookingSeasonRequest,
} from "../bookingSeasonApi"

// The season is set in Pacific time whatever zone the admin's browser is in, so a
// `datetime-local` value here is always read as Pacific wall-clock time. Nothing below uses the
// browser's own zone.

/** Mirrors BookingSeasonDefaults.MessageMaxLength. */
export const MESSAGE_MAX_LENGTH = 2000

function pad(value: number): string {
  return value.toString().padStart(2, "0")
}

// ── Pacific wall time ↔ instant ──────────────────────────────────────

export type PacificInputResult =
  | { ok: true; iso: string }
  /** "empty": nothing entered. "invalid": not a datetime-local value. "skipped": inside the spring-forward gap. */
  | { ok: false; reason: "empty" | "invalid" | "skipped" }

/** "2026-10-01T00:00:00-07:00": an instant written with the Pacific offset in force at that moment. */
export function toPacificIso(instant: Date | string): string {
  const value = instant instanceof Date ? instant : new Date(instant)
  const { date, minutes } = zonedDateAndMinutes(value, SITE_TIME_ZONE)
  const seconds = value.getUTCSeconds()

  // The wall clock read back as if it were UTC, minus the real instant, is the zone's offset.
  const [year, month, day] = date.split("-").map(Number)
  const wallClockAsUtc = Date.UTC(year!, month! - 1, day!) + (minutes * 60_000) + (seconds * 1000)
  const offsetMinutes = Math.round((wallClockAsUtc - Math.floor(value.getTime() / 1000) * 1000) / 60_000)

  const sign = offsetMinutes < 0 ? "-" : "+"
  const abs = Math.abs(offsetMinutes)
  const time = `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}:${pad(seconds)}`
  return `${date}T${time}${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
}

/**
 * Reads a `datetime-local` value ("2026-10-01T00:00") as Pacific wall time.
 *
 * A time the clocks skip on the spring-forward day (2:00–2:59 AM) is refused rather than quietly
 * moved. A time that happens twice on the fall-back day (1:00–1:59 AM) is the first one, PDT.
 */
export function pacificInputToIso(value: string): PacificInputResult {
  if (!value.trim()) return { ok: false, reason: "empty" }

  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/.exec(value.trim())
  if (!match) return { ok: false, reason: "invalid" }

  const date = match[1]!
  const hour = Number(match[2])
  const minute = Number(match[3])
  const second = Number(match[4] ?? 0)
  if (hour > 23 || minute > 59 || second > 59 || Number.isNaN(Date.parse(`${date}T00:00:00Z`))) {
    return { ok: false, reason: "invalid" }
  }

  const minutes = (hour * 60) + minute
  const instant = new Date(zonedTimeToInstant(date, minutes, SITE_TIME_ZONE).getTime() + (second * 1000))

  // A wall time that does not read back as itself never happened in Pacific time.
  const back = zonedDateAndMinutes(instant, SITE_TIME_ZONE)
  if (back.date !== date || back.minutes !== minutes) return { ok: false, reason: "skipped" }

  return { ok: true, iso: toPacificIso(instant) }
}

/** An instant as a `datetime-local` value on the Pacific wall clock, "2026-10-01T00:00". */
export function instantToPacificInput(instant: Date | string): string {
  const { date, minutes } = zonedDateAndMinutes(instant, SITE_TIME_ZONE)
  return `${date}T${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
}

// ── The form ─────────────────────────────────────────────────────────

export interface SeasonFormState {
  /** `datetime-local` value, Pacific. Empty when no season is set. */
  opensAt: string
  closesAt: string
  closedWebMessage: string
  closedPhoneMessageEnglish: string
  /** Empty means Spanish callers hear the English message. */
  closedPhoneMessageSpanish: string
}

export type SeasonField = keyof SeasonFormState

export interface FieldIssue {
  field: SeasonField
  message: string
}

/** The form as the current season stands. With no season set, the dates are empty and the messages are the defaults. */
export function formFromSnapshot(season: BookingSeasonSnapshot): SeasonFormState {
  return {
    opensAt: season.opensAt ? instantToPacificInput(season.opensAt) : "",
    closesAt: season.closesAt ? instantToPacificInput(season.closesAt) : "",
    closedWebMessage: season.closedWebMessage,
    closedPhoneMessageEnglish: season.closedPhoneMessageEnglish,
    closedPhoneMessageSpanish: season.closedPhoneMessageSpanish ?? "",
  }
}

export function sameForm(a: SeasonFormState, b: SeasonFormState): boolean {
  return (
    a.opensAt === b.opensAt &&
    a.closesAt === b.closesAt &&
    a.closedWebMessage === b.closedWebMessage &&
    a.closedPhoneMessageEnglish === b.closedPhoneMessageEnglish &&
    a.closedPhoneMessageSpanish === b.closedPhoneMessageSpanish
  )
}

function dateIssue(field: "opensAt" | "closesAt", label: string, result: PacificInputResult): FieldIssue | null {
  if (result.ok) return null
  switch (result.reason) {
    case "empty":
      return { field, message: `Choose when ${label.toLowerCase()}.` }
    case "invalid":
      return { field, message: `${label} is not a valid date and time.` }
    case "skipped":
      return {
        field,
        message: `${label} falls in the hour the clocks skip when daylight saving time starts. Choose a time outside 2:00–2:59 AM that day.`,
      }
  }
}

function messageIssue(field: SeasonField, label: string, value: string, required: boolean): FieldIssue | null {
  const trimmed = value.trim()
  if (required && !trimmed) return { field, message: `${label} is required.` }
  if (trimmed.length > MESSAGE_MAX_LENGTH) {
    return { field, message: `${label} must be ${MESSAGE_MAX_LENGTH} characters or fewer.` }
  }
  return null
}

/**
 * What stops the form being sent. Mirrors BookingSeasonService.Validate, with the same wording, plus
 * the date checks the server cannot make because it only ever sees instants.
 */
export function validateSeasonForm(state: SeasonFormState): FieldIssue[] {
  const opens = pacificInputToIso(state.opensAt)
  const closes = pacificInputToIso(state.closesAt)

  const issues = [
    dateIssue("opensAt", "Booking opens", opens),
    dateIssue("closesAt", "Booking closes", closes),
  ]

  if (opens.ok && closes.ok && Date.parse(closes.iso) <= Date.parse(opens.iso)) {
    issues.push({ field: "closesAt", message: "Booking closes must be after booking opens." })
  }

  issues.push(
    messageIssue("closedWebMessage", "Website message", state.closedWebMessage, true),
    messageIssue("closedPhoneMessageEnglish", "Phone message (English)", state.closedPhoneMessageEnglish, true),
    messageIssue("closedPhoneMessageSpanish", "Phone message (Spanish)", state.closedPhoneMessageSpanish, false),
  )

  return issues.filter((issue): issue is FieldIssue => issue !== null)
}

/** The PUT body, or null while the form has issues. Messages are trimmed; a blank Spanish message is null. */
export function buildSaveRequest(state: SeasonFormState): SaveBookingSeasonRequest | null {
  if (validateSeasonForm(state).length > 0) return null

  const opens = pacificInputToIso(state.opensAt)
  const closes = pacificInputToIso(state.closesAt)
  if (!opens.ok || !closes.ok) return null

  const spanish = state.closedPhoneMessageSpanish.trim()
  return {
    opensAt: opens.iso,
    closesAt: closes.iso,
    closedWebMessage: state.closedWebMessage.trim(),
    closedPhoneMessageEnglish: state.closedPhoneMessageEnglish.trim(),
    closedPhoneMessageSpanish: spanish ? spanish : null,
  }
}

/** What public booking would be if `request` were saved at `now`: Open exactly when opensAt <= now < closesAt. */
export function statusAfterSave(request: Pick<SaveBookingSeasonRequest, "opensAt" | "closesAt">, now: Date): BookingSeasonStatus {
  const at = now.getTime()
  return Date.parse(request.opensAt) <= at && at < Date.parse(request.closesAt) ? "Open" : "Closed"
}

/** A 400 from PUT carries the reasons nothing was saved. */
export function isSaveRefusal(data: unknown): data is BookingSeasonSaveRefusal {
  return (
    typeof data === "object" &&
    data !== null &&
    Array.isArray((data as { errors?: unknown }).errors) &&
    (data as { errors: unknown[] }).errors.every((e) => typeof e === "string")
  )
}

/**
 * Whether a phone message holds something TTS would read badly — a written web address such as
 * "pse.com" or "www.…". The form asks admins to spell these out ("P S E dot com").
 */
export function hasWrittenWebAddress(message: string): boolean {
  return /\bhttps?:\/\/|\bwww\.|\b[a-z0-9-]+\.(com|org|net|gov|edu|us|info)\b/i.test(message)
}
